import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Dispensary } from "custom-types/Dispensary";
import { useEventTracker } from "hooks/useEventTracker";
import { getDomainCountryCode } from "redux/selectors/config";
import { getCartId } from "redux/selectors/reservation";
import logError from "utils/logError";
import { MENU_TITLES, MenuType } from "utils/menuTypeUtils";
import { Action, Category } from "utils/trackEvent";

import Button from "components/botanic/Button";
import { useIsJaneCheckoutEnabled } from "components/Checkout/JaneCheckout";

import { getDispensaryPath } from "./utils/dispensary";

type NewBagProps = {
  newDispensary?: Dispensary;
  staleDispensary?: Dispensary;
  currentMenuType?: string;
  messaging: string;
  onKeepBagClick?: () => void;
  onNewBagClick: () => Promise<void>;
  shopEveryMenuMVPEnabled?: boolean;
};

type DispensaryMessagingProps = {
  newDispensaryName?: string;
  staleDispensaryName?: string;
  staleDispensaryHref?: string;
};

type MedRecMenuMessagingProps = {
  currentMenuType?: string;
};

const InStoreOrderingMessaging = () => {
  return (
    <div>
      <p>This dispensary offers in-store ordering.</p>
      <p>
        Build your cart, bring it in, and show your budtender exactly what you
        want. They'll complete your order for you in store.
      </p>
    </div>
  );
};

const DispensaryMessaging = ({
  newDispensaryName,
  staleDispensaryName,
  staleDispensaryHref,
}: DispensaryMessagingProps) => {
  return (
    <p>
      You can start a new cart with {newDispensaryName}, but it will empty your
      existing cart with{" "}
      <a href={staleDispensaryHref} className="underline text-green">
        {staleDispensaryName}
      </a>
      . Continue?
    </p>
  );
};

const MedRecMenuMessaging = ({ currentMenuType }: MedRecMenuMessagingProps) => {
  const medical = currentMenuType === MenuType.Med;
  const [pendingBagItemMenuTypeLabels, bagMenuTypeLabels] = medical
    ? [MENU_TITLES.med, MENU_TITLES.rec]
    : [MENU_TITLES.rec, MENU_TITLES.med];
  return (
    <p className="text-sm mt-lg">
      You’ve previously added goodies from the{" "}
      <b>{bagMenuTypeLabels.longLower} menu</b> at this dispensary. You'll need
      to start a new cart to add this item from the{" "}
      {pendingBagItemMenuTypeLabels.longLower} menu.
    </p>
  );
};

export const NewBag: React.FC<NewBagProps> = ({
  newDispensary,
  staleDispensary,
  currentMenuType,
  messaging,
  onKeepBagClick = (): void => {},
  onNewBagClick = (): void => {},
  shopEveryMenuMVPEnabled,
}) => {
  const cartId = useSelector(getCartId);
  const countryCode = useSelector(getDomainCountryCode);

  const { publishEvent } = useEventTracker();
  const [creatingCart, setCreatingCart] = useState(false);
  const isJaneCheckoutEnabled = useIsJaneCheckoutEnabled();

  const isDispMessaging = messaging === "dispensary";

  useEffect(() => {
    publishEvent({
      action: Action.impression,
      cartId: cartId,
      category: Category.addToBag,
      janeCheckoutEnabled: isJaneCheckoutEnabled,
      label: shopEveryMenuMVPEnabled
        ? "existing bag - shop every menu"
        : isDispMessaging
        ? "existing bag"
        : "Med Rec menu message",
    });
  }, []);

  return (
    <div>
      <div className="mx-auto text-center py-section" style={{ maxWidth: 350 }}>
        {shopEveryMenuMVPEnabled && messaging === "dispensary" ? (
          <InStoreOrderingMessaging />
        ) : messaging === "dispensary" ? (
          <DispensaryMessaging
            newDispensaryName={newDispensary?.name}
            staleDispensaryName={staleDispensary?.name}
            staleDispensaryHref={
              staleDispensary
                ? getDispensaryPath(staleDispensary, countryCode)
                : undefined
            }
          />
        ) : (
          <MedRecMenuMessaging currentMenuType={currentMenuType} />
        )}
      </div>

      <div className="flex flex-col md:flex-row md:flex-row-reverse md:border-t border-light-grey -mx-lg px-md pt-md -mb-xs">
        <Button
          data-testid="new_bag__start_new_bag"
          variant="secondary"
          className="text-sm"
          disabled={creatingCart}
          onClick={async () => {
            setCreatingCart(true);

            publishEvent({
              action: Action.click,
              cartId: cartId,
              category: Category.addToBag,
              janeCheckoutEnabled: isJaneCheckoutEnabled,
              label: shopEveryMenuMVPEnabled
                ? "create new - shop every menu"
                : isDispMessaging
                ? "create new"
                : "Med Rec New Cart",
            });

            try {
              await onNewBagClick();
            } catch {
              logError("Error on click in NewBag");
              setCreatingCart(false);
            }
          }}
        >
          {shopEveryMenuMVPEnabled && !creatingCart
            ? "create in-store cart"
            : creatingCart
            ? "creating cart…"
            : "start a new cart"}
        </Button>
        <Button
          className="mx-auto text-sm font-normal text-default mt-lg md:mt-none"
          onClick={onKeepBagClick}
          disabled={creatingCart}
          variant="text"
        >
          cancel
        </Button>
      </div>
    </div>
  );
};
